import {
  APIComponentAttributes,
  APIFieldAttributes,
  APIReferenceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import * as encodingUtils from '@ardoq/html';
import {
  dashFormatDateRangeFieldLabel,
  toEndDateLabel,
  toStartDateLabel,
} from './labelUtils';
import { toEndDateName, toStartDateName } from './nameUtils';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { isDateRangeValue } from './utils';
/**
 * Takes a component or reference and splits the date range fields on them into `_end_date` and '_start_date' fields for the api
 */
export const splitFieldsOnEntity = <
  T extends Partial<APIComponentAttributes> | Partial<APIReferenceAttributes>,
>(
  entityAttributes: T,
  rootWorkspaceId: ArdoqId,
  enhancedScopeData: EnhancedScopeData
): T => {
  const dateRangeFieldMapForWorkspace =
    enhancedScopeData.dateRangeFieldMap?.get(rootWorkspaceId);

  return Object.fromEntries(
    Object.entries(entityAttributes).flatMap(([fieldName, fieldValue]) => {
      // Check if this field is in the date range field map
      if (dateRangeFieldMapForWorkspace?.[fieldName]) {
        const { start, end } = dateRangeFieldMapForWorkspace[fieldName];
        return [
          [start.name, fieldValue?.start ?? null],
          [end.name, fieldValue?.end ?? null],
        ];
      }

      // Defensive: If the value looks like a date range object but is not in the map,
      // split it using the standard naming convention to prevent sending invalid data to the API
      if (isDateRangeValue(fieldValue)) {
        return [
          [toStartDateName(fieldName), fieldValue.start ?? null],
          [toEndDateName(fieldName), fieldValue.end ?? null],
        ];
      }

      // Regular field, pass through unchanged
      return [[fieldName, fieldValue]];
    })
  ) as T;
};

export const splitDateRangeFieldIntoStartAndEndSelectOptions = (
  field: APIFieldAttributes
) => {
  const value = encodingUtils.escapeHTML(field.name);
  const label = encodingUtils.escapeHTML(field.label);
  return [
    {
      value: toStartDateName(value),
      label: dashFormatDateRangeFieldLabel(toStartDateLabel(label)),
    },
    {
      value: toEndDateName(value),
      label: dashFormatDateRangeFieldLabel(toEndDateLabel(label)),
    },
  ];
};
